import React from "react";

import { faChevronRight } from "@performyard/fontawesome/pro-regular-svg-icons";

import { Icon, Link } from "../../atoms";

const breadcrumbTextClassName = "inline-block max-w-[200px] py-text-truncate align-bottom text-paragraph-2";

export type Breadcrumb = {
	/**
	 * Display content for the item — wrapped in a Link unless this is the final item.
	 */
	name: React.ReactNode;
	/**
	 * Destination href for the Link.
	 */
	href: string;
	/**
	 * Force a full page reload — needed for SPA → Tornado-template transitions.
	 */
	forceHardReload?: boolean;
	/**
	 * Stable id used for keys and as a data-testid on the item.
	 * Falls back to the index when omitted.
	 */
	pageId?: string;
};

export type BreadcrumbsProps = {
	/**
	 * Ordered list of breadcrumb items, root-first. The last item is rendered as the
	 * active page (no Link, special styling, no separator after it).
	 */
	items: Breadcrumb[];
	/**
	 * Optional class name override applied to the outer container.
	 */
	dangerouslySetClassName?: string;
};

/**
 * Renders a breadcrumb trail. The final item is shown as the active page (plain text),
 * preceding items are rendered as links separated by a chevron icon. Wraps to additional
 * lines when the container is narrower than the chain.
 */
const Breadcrumbs = ({ items, dangerouslySetClassName }: BreadcrumbsProps) => {
	if (items.length === 0) return null;

	return (
		<nav aria-label="Breadcrumb" className={dangerouslySetClassName}>
			<ol className="m-0 flex list-none flex-wrap items-center gap-1 p-0">
				{items.map((item, index) => {
					const isLast = index === items.length - 1;
					const key = item.pageId ?? `${index}-${item.href}`;

					return (
						<li key={key} data-testid={item.pageId} className="flex shrink-0 items-center gap-1">
							{isLast ? (
								<span
									className={`${breadcrumbTextClassName} font-semibold text-neutral-9`}
									aria-current="page"
								>
									{item.name}
								</span>
							) : (
								<Link
									href={item.href}
									forceHardReload={item.forceHardReload}
									underline="never"
									dangerouslySetClassName={`${breadcrumbTextClassName} text-neutral-5 hover:text-primary-5`}
								>
									{item.name}
								</Link>
							)}
							{!isLast && <Icon icon={faChevronRight} size={16} color="neutral-5" />}
						</li>
					);
				})}
			</ol>
		</nav>
	);
};

Breadcrumbs.displayName = "Breadcrumbs";

export { Breadcrumbs };
